import StoreUpdateEvents from "../constants/events/StoreUpdateEvents";
import ActionResultEvents from "../constants/events/ActionResultEvents";
import LoadingStates from "../constants/states/LoadingStates";
import SecureBaseStore from "./SecureBaseStore";
import UIInteractionEvents from "../constants/events/UIInteractionEvents";
import Time from "../utils/Time";


class MemberSpaceCalendarState {
    constructor(
        loadingState,
        showReservationCrossingSelectionErrorAlert,
        currentSelectionRange,
        blockedDates,
        reservationRequests,
        activeReservations
    ) {
        this.loadingState = loadingState;
        this.showReservationCrossingSelectionErrorAlert = showReservationCrossingSelectionErrorAlert;
        this.currentSelectionRange = currentSelectionRange;
        this.blockedDates = blockedDates;
        this.reservationRequests = reservationRequests;
        this.activeReservations = activeReservations;
    }

    equals(other) {
        return this.loadingState === other.loadingState &&
            this.showReservationCrossingSelectionErrorAlert === other.showReservationCrossingSelectionErrorAlert &&
            this.currentSelectionRange[0] === other.currentSelectionRange[0] &&
            this.currentSelectionRange[1] === other.currentSelectionRange[1];
        // TODO - should we compare the various events too?
    }
}

class ImmutableMemberSpaceCalendarStateBuilder {

    constructor() {
        this.loadingState = LoadingStates.INITIALIZED;
        this.showReservationCrossingSelectionErrorAlert = false;
        this.currentSelectionRange = [];
        this.blockedDates = [];
        this.reservationRequests = [];
        this.activeReservations = [];
    }

    static fromState(state) {
        return new ImmutableMemberSpaceCalendarStateBuilder()
            .setMemberSpaceCalendarState(state.blockedDates, state.reservationRequests, state.activeReservations)
            .setLoadingState(state.loadingState)
            .setNewSelectionDates(state.currentSelectionStartDate, state.currentSelectionEndDate)
            .setShowReservationCrossingSelectionErrorAlert(state.showReservationCrossingSelectionErrorAlert)
    }

    setLoadingState(loadingState) {
        this.loadingState = loadingState;
        return this;
    }

    setShowReservationCrossingSelectionErrorAlert(showReservationCrossingSelectionErrorAlert) {
        this.showReservationCrossingSelectionErrorAlert = showReservationCrossingSelectionErrorAlert;
        return this;
    }

    setLoading() {
        this.loadingState = LoadingStates.LOADING;
        return this;
    }

    setNewSelectionDates(newStartDate, newEndDate) {
        this.currentSelectionRange = [newStartDate, newEndDate];
        return this;
    }

    resetSelectedDates() {
        this.currentSelectionRange = [];
        return this;
    }

    setMemberSpaceCalendarState(
        blockedDates,
        reservationRequests,
        activeReservations
    ) {
        this.loadingState = LoadingStates.FINISHED_LOADING;
        this.blockedDates = blockedDates;
        this.reservationRequests = reservationRequests;
        this.activeReservations = activeReservations;
        return this;
    }

    build() {
        return Object.freeze(
            new MemberSpaceCalendarState(
                this.loadingState,
                this.showReservationCrossingSelectionErrorAlert,
                this.currentSelectionRange,
                this.blockedDates,
                this.reservationRequests,
                this.activeReservations));
    }
}

class MemberSpaceCalendarStore extends SecureBaseStore {

    constructor() {
        super(StoreUpdateEvents.MEMBER_SPACE_CALENDAR_STORE_UPDATED, ImmutableMemberSpaceCalendarStateBuilder);
        this.subscribeToEvent(ActionResultEvents.MEMBER_SPACE_CALENDAR_LOADED, this.handleMemberSpaceCalendarLoaded);
        this.subscribeToEvent(UIInteractionEvents.RENDERED_VIEW_REQUIRES_MEMBER_SPACE_CALENDAR, this.handleMemberSpaceLoading);
        this.subscribeToEvent(UIInteractionEvents.BLOCK_ALL_MEMBER_SPACE_CALENDAR_CLICKED, this.handleMemberSpaceLoading);
        this.subscribeToEvent(UIInteractionEvents.UNBLOCK_ALL_MEMBER_SPACE_CALENDAR_CLICKED, this.handleMemberSpaceLoading);
        this.subscribeToEvent(UIInteractionEvents.BLOCK_DATES_MEMBER_SPACE_CALENDAR_CLICKED, this.handleMemberSpaceLoading);
        this.subscribeToEvent(UIInteractionEvents.UNBLOCK_DATES_MEMBER_SPACE_CALENDAR_CLICKED, this.handleMemberSpaceLoading);
        this.subscribeToEvent(UIInteractionEvents.CURRENT_MEMBER_CALENDAR_SELECTION_RANGE_CHANGED, this.handleMemberCalendarSelectionRangeChanged);
        this.subscribeToEvent(UIInteractionEvents.CLOSE_RESERVATION_CROSSING_SELECTION_ERROR_ALERT, this.handleReservationCrossingSelectionErrorAlertClosed);

    }

    handleMemberSpaceLoading = (topic, data) => {
        return new ImmutableMemberSpaceCalendarStateBuilder()
            .setLoading()
            .build();
    };

    handleReservationCrossingSelectionErrorAlertClosed = (topic, data) => {
        return ImmutableMemberSpaceCalendarStateBuilder.fromState(this.state)
            .setShowReservationCrossingSelectionErrorAlert(false)
            .build();
    };

    handleMemberCalendarSelectionRangeChanged = (topic, data) => {
        for (const reservationRequest of this.state.reservationRequests) {
            const reservationRequestStart = Time.parseDate(reservationRequest.startDate);
            const reservationRequestEnd = Time.parseDate(reservationRequest.endDate);
            if (Time.rangesOverlap(data.currentSelectionStartDate, data.currentSelectionEndDate, reservationRequestStart, reservationRequestEnd)) {
                return ImmutableMemberSpaceCalendarStateBuilder.fromState(this.state)
                    .setShowReservationCrossingSelectionErrorAlert(true)
                    .resetSelectedDates()
                    .build();
            }
        }

        for (const activeReservation of this.state.activeReservations) {
            const activeReservationStart = Time.parseDate(activeReservation.startDate);
            const activeReservationEnd = Time.parseDate(activeReservation.endDate);
            if (Time.rangesOverlap(data.currentSelectionStartDate, data.currentSelectionEndDate, activeReservationStart, activeReservationEnd)) {
                return ImmutableMemberSpaceCalendarStateBuilder.fromState(this.state)
                    .setShowReservationCrossingSelectionErrorAlert(true)
                    .resetSelectedDates()
                    .build();
            }
        }

        return ImmutableMemberSpaceCalendarStateBuilder.fromState(this.state)
            .setNewSelectionDates(data.currentSelectionStartDate, data.currentSelectionEndDate)
            .build();
    };

    handleMemberSpaceCalendarLoaded = (topic, data) => {
        return new ImmutableMemberSpaceCalendarStateBuilder()
            .setMemberSpaceCalendarState(
                data.blockedDates,
                data.reservationRequests,
                data.activeReservations)
            .build();
    };


}

export default new MemberSpaceCalendarStore();